// A small "Delete this card?" confirmation popover anchored NEXT TO the clicked element
// (a card trash button) — shown instead of a centered modal so the confirm appears right
// where the user clicked. Light/dark aware via the document's data-theme attribute.
export function confirmDeletePopover(anchor: HTMLElement, onConfirm: () => void, message = "Delete this card?", confirmLabel = "Delete", wrap = false): void {
  // Only one open at a time.
  document.querySelectorAll(".lsg-confirm-pop").forEach(el => el.remove());

  const pop = document.createElement("div");
  // `wrap` = a longer, multi-line message: stack the message above the buttons with a max-width
  // instead of the default single-line row.
  pop.className = wrap ? "lsg-confirm-pop lsg-confirm-wrap" : "lsg-confirm-pop";
  pop.innerHTML =
    `<span class="lsg-confirm-msg"></span>` +
    `<div class="lsg-confirm-actions">` +
      `<button class="lsg-confirm-yes" type="button"></button>` +
      `<button class="lsg-confirm-no" type="button">Cancel</button>` +
    `</div>`;
  (pop.querySelector(".lsg-confirm-msg") as HTMLElement).textContent = message;
  (pop.querySelector(".lsg-confirm-yes") as HTMLElement).textContent = confirmLabel;
  document.body.appendChild(pop);

  // Anchor it just below the trashcan, right edges aligned (so it grows leftward and stays
  // on screen); flip above if there's no room below. Clamp to the viewport.
  const r = anchor.getBoundingClientRect();
  const pw = pop.offsetWidth, ph = pop.offsetHeight;
  let left = r.right - pw;
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - 8) top = r.top - ph - 6;
  left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
  top = Math.max(8, top);
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;

  const close = () => {
    pop.remove();
    document.removeEventListener("mousedown", onOutside, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", close, true);
  };
  const onOutside = (e: MouseEvent) => { if (!pop.contains(e.target as Node) && e.target !== anchor) close(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };

  pop.querySelector(".lsg-confirm-yes")!.addEventListener("click", () => { close(); onConfirm(); });
  pop.querySelector(".lsg-confirm-no")!.addEventListener("click", close);
  // Defer global listeners so the opening click doesn't immediately dismiss it.
  setTimeout(() => {
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", close, true); // close on scroll (anchor would move)
  }, 0);
}

// An informational popover anchored next to the clicked element (no confirm buttons — just a
// titled body with a close ×). Dismisses on outside click / Escape / scroll. Used to show the
// list of keywords added to a page in the History — Optimized Pages table.
export function infoPopover(anchor: HTMLElement, title: string, body: string): void {
  document.querySelectorAll(".lsg-confirm-pop, .lsg-info-pop").forEach(el => el.remove());

  const pop = document.createElement("div");
  pop.className = "lsg-info-pop";
  pop.innerHTML =
    `<button class="lsg-info-close" type="button" aria-label="Close">×</button>` +
    `<div class="lsg-info-title"></div>` +
    `<div class="lsg-info-body"></div>`;
  (pop.querySelector(".lsg-info-title") as HTMLElement).textContent = title;
  (pop.querySelector(".lsg-info-body") as HTMLElement).textContent = body;
  document.body.appendChild(pop);

  const r = anchor.getBoundingClientRect();
  const pw = pop.offsetWidth, ph = pop.offsetHeight;
  let left = r.right - pw;
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - 8) top = r.top - ph - 6;
  left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
  top = Math.max(8, top);
  pop.style.left = `${Math.round(left)}px`;
  pop.style.top = `${Math.round(top)}px`;

  const close = () => {
    pop.remove();
    document.removeEventListener("mousedown", onOutside, true);
    document.removeEventListener("keydown", onKey, true);
    window.removeEventListener("scroll", close, true);
  };
  const onOutside = (e: MouseEvent) => { if (!pop.contains(e.target as Node) && e.target !== anchor) close(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };

  pop.querySelector(".lsg-info-close")!.addEventListener("click", close);
  setTimeout(() => {
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", close, true);
  }, 0);
}
